import React, { forwardRef, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { MegaMenuSecondaryNavigationPanel } from './MegaMenuSecondaryNavigationPanel';
import { cwRTLTranslate, flyoutDesktopBlockSize, flyoutDesktopClipPathTransition, flyoutPanelTransition, MegaMenuCSSCustomProperties, megaMenuZIndexes, secondaryNavigationId, } from './MegaMenu.config';
import { useMegaMenu } from './MegaMenuContext';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { MotionDurations, MotionEasings, createCSSTransition, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { StaticCloseButton } from './MegaMenuSharedCloseButton';
var FlyOutCustomProperty;
(function (FlyOutCustomProperty) {
    FlyOutCustomProperty["translate"] = "--flyout-translate";
    FlyOutCustomProperty["pointerEvents"] = "--flyout-pointer-events";
    FlyOutCustomProperty["clipPathPercentage"] = "--flyout-clip-path-percentage";
    FlyOutCustomProperty["menuActive"] = "--flyout-active";
    FlyOutCustomProperty["offset"] = "--flyout-offset";
    // TODO: use prefix + rename CustomProperty
    FlyOutCustomProperty["transitionDuration"] = "--transition-duration";
})(FlyOutCustomProperty || (FlyOutCustomProperty = {}));
/**
 * Contains the secondary navigation items
 */
export const MegaMenuSecondaryNavigation = forwardRef(({ flyout, backButtonLabel, megaMenuId, ariaLabels, ...props }, ref) => {
    const { flyoutActive, setFlyoutItems } = useMegaMenu();
    const stackRef = useRef(null);
    useEffect(() => {
        if (!!stackRef.current) {
            // only update when available to avoid testing issues
            const panels = Array.from(stackRef.current.children);
            const links = panels.map((panel) => {
                if (panel) {
                    return Array.from(panel === null || panel === void 0 ? void 0 : panel.querySelectorAll('a'));
                }
            });
            setFlyoutItems(links.filter((link) => !!link));
        }
    }, [setFlyoutItems]);
    return (React.createElement(Container, { style: {
            [FlyOutCustomProperty.pointerEvents]: flyoutActive ? 'auto' : 'none',
            [FlyOutCustomProperty.translate]: flyoutActive ? '0' : '100%',
            [FlyOutCustomProperty.menuActive]: flyoutActive ? 1 : 0,
        }, ...props, ref: ref },
        React.createElement(Stack, { ref: stackRef }, flyout.map((item, index) => item ? (React.createElement(MegaMenuSecondaryNavigationPanel, { id: `${megaMenuId}-${secondaryNavigationId}-${index}`, key: index, navigationPanelIndex: index, panelContent: item, backButtonLabel: backButtonLabel, ariaLabels: ariaLabels })) : (
        // find a way to handle the sub menu items with indices and main navigation items.
        // this is a temporary fix to keep match the index of the main navigation item and sub navigation item
        React.createElement("span", { key: index, style: { display: 'none' } })))),
        React.createElement(StaticCloseButton, { "aria-label": ariaLabels.closeMenu, hideOnPointer: true })));
});
const Container = styled.div `
  position: fixed;
  inset: 0;
  background: var(${MegaMenuCSSCustomProperties.background});
  translate: calc(var(${FlyOutCustomProperty.translate}) * var(${cwRTLTranslate}, 1)) 0;
  pointer-events: var(${FlyOutCustomProperty.pointerEvents});
  transition: ${flyoutPanelTransition};
  transition-duration: var(${MegaMenuCSSCustomProperties.flyoutTransitionDuration});

  z-index: ${megaMenuZIndexes.flyout};
  inline-size: 100%;

  ${MediaQueries.tablet} {
    inline-size: min(100%, var(${MegaMenuCSSCustomProperties.tabletMaxInlineSize}));
    inset-inline: auto 0;
  }

  ${MediaQueries.desktop} {
    ${FlyOutCustomProperty.menuActive}: 0;
    ${FlyOutCustomProperty.clipPathPercentage}: calc(100% * var(${FlyOutCustomProperty.menuActive}));
    position: absolute;
    inline-size: 100%;
    clip-path: polygon(
      0 0,
      100% 0,
      100% var(${FlyOutCustomProperty.clipPathPercentage}),
      0 var(${FlyOutCustomProperty.clipPathPercentage})
    );
    transition: ${flyoutDesktopClipPathTransition};
    translate: none;
    inset: var(${MegaMenuCSSCustomProperties.blockSize}) 0 0 0;
    block-size: ${flyoutDesktopBlockSize};
  }
`;
const Stack = styled.div `
  display: grid;
  block-size: 100%;

  > * {
    grid-area: 1 / 1;
  }

  ${MediaQueries.desktop} {
    ${FlyOutCustomProperty.offset}: -${createCustomSpacing(40 / 16)};
    translate: 0
      calc(var(${FlyOutCustomProperty.offset}) * (1 - var(${FlyOutCustomProperty.menuActive})));
    transition: ${createCSSTransition('translate', MotionEasings.easeInOut, MotionDurations.xSlow)};

    @media (prefers-reduced-motion: reduce) {
      ${FlyOutCustomProperty.offset}: 0;
    }
  }
`;
